import { Badge, Label } from "visuais";
import { motion } from "motion/react";
import { FornecedorComboboxItem } from "@apiInclui/fornecedor";
import { identificacaoUtils } from "@PN/inclusao";
import {
  Building2,
  Phone,
  MapPin,
  Calendar,
  Shield,
  Check,
} from "lucide-react";

interface ContentDialogProps {
  opt: FornecedorComboboxItem;
  isSelected: boolean;
}

const ContentDialog = ({ opt, isSelected }: ContentDialogProps) => {
  return (
    <motion.div
      className="w-full p-0.5"
      whileHover={{ scale: 1.005 }}
      transition={{ duration: 0.15 }}
    >
      {/* Header principal */}
      <div className="flex justify-between items-center w-full mb-1.5">
        <div className="flex gap-1.5 items-center flex-1 min-w-0">
          <div className="flex items-center justify-center w-4 h-4 rounded-full bg-cyan-100 dark:bg-cyan-900/30">
            <Building2 className="h-2.5 w-2.5 text-cyan-600 dark:text-cyan-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-slate-900 dark:text-slate-100 truncate">
                {opt.nome}
              </span>
              {isSelected && (
                <Badge
                  variant="outline"
                  className="text-[9px] px-1 py-0 h-4 leading-4 border-cyan-600 dark:border-cyan-400 text-cyan-700 dark:text-cyan-300"
                >
                  Selecionado
                </Badge>
              )}
            </div>
            {opt.nomeReduzido && opt.nomeReduzido !== opt.nome && (
              <span className="text-[10px] text-slate-500 dark:text-slate-400 italic truncate">
                {opt.nomeReduzido}
              </span>
            )}
          </div>
        </div>
        <Badge
          variant="outline"
          className="border-cyan-600 dark:border-cyan-400 text-cyan-700 dark:text-cyan-300 text-[9px] px-1.5 py-0.5"
        >
          {opt.cod}/{opt.loja}
        </Badge>
      </div>

      {/* Informações em grid 2x2 */}
      <div className="grid grid-cols-2 gap-1.5 text-[10px]">
        {/* CNPJ */}
        {opt.cnpj && (
          <div className="flex items-center gap-1 text-cyan-600 dark:text-cyan-400">
            <Shield className="h-2.5 w-2.5 text-cyan-500 flex-shrink-0" />
            <div className="flex flex-col">
              <span className="text-[9px] text-slate-500 dark:text-slate-400">
                CNPJ
              </span>
              <span className="font-medium text-[9px]">
                {identificacaoUtils.fornecedor.formatCNPJ(opt.cnpj)}
              </span>
            </div>
          </div>
        )}

        {/* Telefone */}
        {opt.telefone && (
          <div className="flex justify-end items-center gap-1 text-cyan-600 dark:text-cyan-400">
            <Phone className="h-2.5 w-2.5 text-cyan-500 flex-shrink-0" />
            <div className="flex flex-col">
              <span className="text-[9px] text-slate-500 dark:text-slate-400">
                Telefone
              </span>
              <span className="font-medium text-[9px]">
                {identificacaoUtils.fornecedor.formatTelefone(
                  opt.original.A2_DDD,
                  opt.telefone
                )}
              </span>
            </div>
          </div>
        )}

        {/* Endereço */}
        {opt.endereco && (
          <div className="flex items-center gap-1 text-cyan-600 dark:text-cyan-400">
            <MapPin className="h-2.5 w-2.5 text-cyan-500 flex-shrink-0" />
            <div className="flex flex-col">
              <span className="text-[9px] text-slate-500 dark:text-slate-400">
                Endereço
              </span>
              <span className="font-medium text-[9px] truncate">
                {identificacaoUtils.fornecedor.formatEndereco(
                  opt.endereco,
                  opt.municipio,
                  opt.estado
                )}
              </span>
            </div>
          </div>
        )}

        {/* Última Compra */}
        {opt.ultimaCompra && (
          <div className="flex justify-end items-center gap-1 text-cyan-600 dark:text-cyan-400">
            <Calendar className="h-2.5 w-2.5 text-cyan-500 flex-shrink-0" />
            <div className="flex flex-col">
              <span className="text-[9px] text-slate-500 dark:text-slate-400">
                Última Compra
              </span>
              <span className="font-medium text-[9px]">
                {identificacaoUtils.date.formatUltimaCompra(opt.ultimaCompra)}
              </span>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export { ContentDialog as FornecedorContentDialog };
